import React, { useEffect, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { GuestGapDevice } from '../components/Guest/GuestGapDevice';
import { Button } from '../components/UI/primitives/Button';
import { useUser } from '../contexts/UserContext';
import { resolveKeyedLandingCopy } from '../utils/keyedLandingCopy';
import {
  buildProspectorEntryPath,
  mergeLandingSourceKeys,
  parseLandingSourceKeyFromSearch,
  persistLandingSourceKey,
  readPersistedLandingSourceKey,
  type LandingSourceKey,
} from '../utils/landingSourceKey';
import styles from './GuestHomePage.module.css';

/**
 * Magnet / partner keyed landing (`/go`, `/go/:magnetSlug`).
 * Captures source_key, shows guest-1b–compatible copy, single CTA → prospector.
 */
export const KeyedLandingPage: React.FC = () => {
  const { isAuthenticated } = useUser();
  const navigate = useNavigate();
  const { magnetSlug } = useParams<{ magnetSlug?: string }>();
  const [searchParams] = useSearchParams();
  const search = searchParams.toString();

  const key = useMemo(() => {
    const pathKey: LandingSourceKey | null = magnetSlug?.trim()
      ? { source_magnet: magnetSlug.trim() }
      : null;
    const fromQuery = parseLandingSourceKeyFromSearch(search ? `?${search}` : '');
    const merged = mergeLandingSourceKeys(
      mergeLandingSourceKeys(readPersistedLandingSourceKey(), fromQuery),
      pathKey
    );
    persistLandingSourceKey(merged);
    return merged;
  }, [magnetSlug, search]);

  const copy = resolveKeyedLandingCopy(key);
  const prospectorPath = buildProspectorEntryPath(key);

  useEffect(() => {
    if (isAuthenticated) {
      navigate(prospectorPath, { replace: true });
    }
  }, [isAuthenticated, navigate, prospectorPath]);

  if (isAuthenticated) {
    return null;
  }

  return (
    <main className={styles.landing} data-testid="keyed-landing" data-variant={copy.variant}>
      <header className={styles.nav}>
        <img src="/krysalis-logo.svg" alt="Krysalis" className={styles.logo} />
        <button type="button" className={styles.signInBtn} data-testid="guest-sign-in-nav" onClick={() => navigate('/login')}>
          Already on the bridge? Sign in
        </button>
      </header>

      <section className={styles.hero} aria-labelledby="keyed-hero-heading">
        <div className={styles.heroInner}>
          <p className={styles.kicker}>{copy.kicker}</p>
          <h1 id="keyed-hero-heading" className={styles.h1}>
            {copy.headline}
            <br />
            <span className={styles.h1Secondary}>{copy.headlineSecondary}</span>
          </h1>
          <p className={styles.heroSub}>{copy.sub}</p>
          <GuestGapDevice className={styles.gap} />
          <Button
            className={styles.cta}
            variant="primary"
            size="lg"
            onClick={() =>
              navigate(`/register?next=${encodeURIComponent(prospectorPath)}`)
            }
            iconRight={<ArrowRight size={18} aria-hidden />}
          >
            {copy.cta}
          </Button>
          {copy.showExplainerLink ? (
            <p className={styles.finePrint} style={{ marginTop: 24 }}>
              <button
                type="button"
                className={styles.footerLink}
                onClick={() => navigate('/resources/krysalis-explainer')}
              >
                Read the Krysalis explainer
              </button>
            </p>
          ) : null}
        </div>
      </section>

      <footer className={styles.footer}>
        <p className={styles.footerCopy}>© Krysalis — the bridge between intent and execution</p>
        <button type="button" className={styles.footerLink} onClick={() => navigate('/resources')}>
          Resources
        </button>
        <button type="button" className={styles.footerLink} onClick={() => navigate('/')}>
          Home
        </button>
      </footer>
    </main>
  );
};
